// react
import {
	ChangeEvent,
	Dispatch,
	FormEvent,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useScrollToError } from '../../../hooks/useScrollToError';

// mui
import {
	Alert,
	Box,
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	CalendarMonth as IconCalendar,
	Payments as IconSpendPlan,
} from '@mui/icons-material';

// types
import { Account, SpendPlan } from '../../../@types';

// services
import { createSpendPlan, updateSpendPlan } from '../../../services/api';

// components
import { FundingSummary } from '../FundingSummary/FundingSummary';
import { TimelinePresets, TimelineDates, parseFiscalYearStartMonth } from '../../ui/TimelinePresets/TimelinePresets';
import {
	CustomFieldsFormSection,
	validateCustomFields,
} from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';

// Props & Types

export interface FormCreateSpendPlanProps {
	onClose?: () => void;
	onSuccess?: (spendPlanId?: number) => void;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
	spendPlan?: SpendPlan | null;
}

// Component

export const FormCreateSpendPlan = ({
	onClose,
	onSuccess,
	setIsLoading,
	spendPlan,
}: FormCreateSpendPlanProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { accounts, workPlans } = useContext(ContextSpendPlan);

	// derived
	const isEditMode = Boolean(spendPlan);
	const formId = isEditMode ? 'form-edit-spend-plan' : 'form-create-spend-plan';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state - fetch data
	const [fetchedAccounts, setFetchedAccounts] = useState<Account[]>([]);
	const [error, setError] = useState<string | null>(null);

	// scroll to error when it occurs
	const errorRef = useScrollToError(error);

	// state - timeline preset selection
	// If editing with existing dates, default to 'custom' so the date fields are shown
	const [timelinePreset, setTimelinePreset] = useState(() => {
		if (spendPlan?.dateStart && spendPlan?.dateEnd) {
			return 'custom';
		}
		return '';
	});

	// state - form values
	const [spendPlanName, setSpendPlanName] = useState<string>(spendPlan?.name || '');
	const [selectedAccountId, setSelectedAccountId] = useState<number | ''>(
		spendPlan
			? typeof spendPlan.account === 'number'
				? spendPlan.account
				: spendPlan.account?.id || ''
			: ''
	);
	const [dateStart, setDateStart] = useState<string>(
		spendPlan?.dateStart ? spendPlan.dateStart.slice(0, 10) : ''
	);
	const [dateEnd, setDateEnd] = useState<string>(
		spendPlan?.dateEnd ? spendPlan.dateEnd.slice(0, 10) : ''
	);

	// state - work plan
	const [selectedWorkPlanId, setSelectedWorkPlanId] = useState<number | ''>(() => {
		if (!spendPlan?.workPlan) return '';
		return typeof spendPlan.workPlan === 'number' ? spendPlan.workPlan : spendPlan.workPlan.id;
	});

	// state - custom fields
	const [customData, setCustomData] = useState<Record<string, unknown>>(
		spendPlan?.customData || {}
	);

	const handleCustomFieldChange = (columnId: string, value: unknown) => {
		setCustomData((prev) => ({ ...prev, [columnId]: value }));
	};

	const customSpendPlanColumns =
		(project?.customFieldDefinitions || []).find((d) => d.entityType === 'spend_plan')
			?.definitions || [];

	// fetch accounts
	const { isLoading: isLoadingAccounts } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				setFetchedAccounts(res.data);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&populate=loans`,
	});

	// Use fetched accounts or context accounts
	const allAccounts = fetchedAccounts.length > 0 ? fetchedAccounts : accounts;

	// Exclude deactivated accounts from the selector, but keep the currently
	// selected account visible in edit mode so the form still renders correctly
	// if a previously-selected account has since been deactivated.
	const availableAccounts = useMemo(
		() => allAccounts.filter((a) => a.status !== 'DEACTIVATED' || a.id === selectedAccountId),
		[allAccounts, selectedAccountId]
	);

	// Get selected account
	const selectedAccount = useMemo(() => {
		return availableAccounts.find((a) => a.id === selectedAccountId) || null;
	}, [availableAccounts, selectedAccountId]);

	// Calculate account balance
	const accountBalance = useMemo(() => {
		if (!selectedAccount?.balances) return 0;
		return selectedAccount.balances.reduce((sum, b) => sum + (b.balance || 0), 0);
	}, [selectedAccount]);

	// Totals (now just account balance — monthly allocations drive the detail)
	const totals = useMemo(
		() => ({
			earmarked: 0,
			planned: 0,
			actualSpend: 0,
			future: 0,
			total: 0,
			pendingCount: 0,
		}),
		[]
	);

	// Local loading state for the account fetch. This drives the inline
	// LinearProgress only — it is intentionally NOT propagated to the parent's
	// setIsLoading, which is reserved for the on-chain submit animation.
	const isLoading = isLoadingAccounts;

	// Auto-generate spend plan name when account or start date changes
	useEffect(() => {
		if (!isEditMode) {
			const accountName = availableAccounts.find((a) => a.id === selectedAccountId)?.name || '';
			if (accountName || dateStart) {
				setSpendPlanName(`Spend Plan - ${accountName || 'Account'} - ${dateStart || 'TBD'}`);
			}
		}
	}, [selectedAccountId, dateStart, availableAccounts, isEditMode]);

	const handleAccountChange = (e: SelectChangeEvent<number | ''>) => {
		setSelectedAccountId(e.target.value as number | '');
	};

	const handleTimelineChange = (dates: TimelineDates & { presetId: string }) => {
		setTimelinePreset(dates.presetId);
		setDateStart(dates.startDate);
		setDateEnd(dates.endDate);
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);

		// Validation
		if (!selectedAccountId) {
			setError('Please select an account');
			return;
		}
		if (!dateStart || !dateEnd) {
			setError(t('spendPlan.createSpendPlan.errorPeriod'));
			return;
		}

		if (!selectedWorkPlanId) {
			setError('Please select a work plan');
			return;
		}

		// Custom field validation
		const missingFields = validateCustomFields(customSpendPlanColumns, customData);
		if (missingFields.length > 0) {
			setError(`Required custom fields: ${missingFields.join(', ')}`);
			return;
		}

		try {
			if (setIsLoading) setIsLoading(true);

			// Build payload — work plan driven, no manual allocations
			const basePayload = {
				workPlanId: selectedWorkPlanId as number,
				accountId: selectedAccountId as number,
				name: spendPlanName || `Spend Plan - ${selectedAccount?.name || 'Account'} - ${dateStart}`,
				dateStart: dateStart,
				dateEnd: dateEnd,
				currency: currency,
				customData,
			};

			// Call API
			if (!accessToken || !idToken) {
				throw new Error('Authentication required');
			}

			const result =
				isEditMode && spendPlan
					? await updateSpendPlan({ accessToken, idToken }, spendPlan.id, basePayload)
					: await createSpendPlan(
							{ accessToken, idToken },
							{
								...basePayload,
								projectId: project?.id as number,
							}
						);

			if (result.error) {
				throw new Error(result.error.message);
			}

			setBanner({
				message: `Spend plan ${isEditMode ? 'updated' : 'created'} successfully`,
				severity: 'success',
			});

			if (onSuccess) onSuccess(result.data?.id);
			if (onClose) onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save spend plan');
		} finally {
			if (setIsLoading) setIsLoading(false);
		}
	};

	const currency =
		selectedAccount?.currency ||
		project?.currencies?.find((c) => c.type === 'REPORTING')?.currency ||
		'USD';

	return (
		<form id={formId} noValidate onSubmit={handleSubmit}>
			{isLoading && <LinearProgress sx={{ mb: 2 }} />}

			<Grid container spacing={3}>
				{/* Left Column - Account Selection & Period */}
				<Grid item xs={12} md={7}>
					{/* Spend Plan Name */}
					<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3, p: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
							<IconSpendPlan color="primary" />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							{t('spendPlan.createSpendPlan.nameSection')}
							</Typography>
						</Stack>

						<TextField
							fullWidth
							label={t('name')}
							value={spendPlanName}
							onChange={(e: ChangeEvent<HTMLInputElement>) => setSpendPlanName(e.target.value)}
							placeholder={t('spendPlan.createSpendPlan.namePlaceholder')}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconSpendPlan sx={{ color: 'text.secondary', fontSize: 20 }} />
									</InputAdornment>
								),
							}}
						/>
					</Paper>

					{/* Account Selection */}
					<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3, p: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
							<IconAccount color="primary" />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							{t('spendPlan.createSpendPlan.chooseAccount')}
							</Typography>
						</Stack>

						<FormControl fullWidth sx={{ mb: 2 }}>
							<InputLabel>Account</InputLabel>
							<Select label={t('account')} value={selectedAccountId} onChange={handleAccountChange}>
								{availableAccounts.map((account) => (
									<MenuItem key={account.id} value={account.id}>
										<Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
											<IconAccount sx={{ color: 'text.secondary', fontSize: 20 }} />
											<Box sx={{ flex: 1 }}>
												<Typography variant="body2" fontWeight={500}>
													{account.name}
												</Typography>
											</Box>
										</Stack>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Paper>

					{/* Work Plan Selection */}
					<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3, p: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
							<IconSpendPlan color="primary" />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							{t('spendPlan.createSpendPlan.linkWorkPlan')}
							</Typography>
						</Stack>

						<Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
						{t('spendPlan.createSpendPlan.linkWorkPlanHelper')}
					</Typography>

					<FormControl fullWidth>
							<InputLabel>Work Plan</InputLabel>
							<Select
								label={t('workPlan')}
								value={selectedWorkPlanId}
								onChange={(e: SelectChangeEvent<number | ''>) =>
									setSelectedWorkPlanId(e.target.value as number | '')
								}>
								{workPlans.map((wp) => (
									<MenuItem key={wp.id} value={wp.id}>
										<Typography variant="body2" fontWeight={500}>
											{wp.name}
										</Typography>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Paper>

					{/* Planning Period */}
					<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3, p: 2.5 }}>
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
							<IconCalendar color="primary" />
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							{t('spendPlan.createSpendPlan.setPeriod')}
							</Typography>
						</Stack>
						<Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
						{t('spendPlan.createSpendPlan.setPeriodHelper')}					</Typography>
						<TimelinePresets
							endDate={dateEnd}
							fiscalYearStartMonth={parseFiscalYearStartMonth(project?.fiscalYearStart)}
							onChange={handleTimelineChange}
							required
							selectedPreset={timelinePreset}
							startDate={dateStart}
						/>
					</Paper>

					{/* Custom Fields */}
					{customSpendPlanColumns.filter((c) => c.isActive).length > 0 && (
						<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3, p: 2.5 }}>
							<Grid container spacing={2}>
								<CustomFieldsFormSection
									columns={customSpendPlanColumns}
									customData={customData}
									onChange={handleCustomFieldChange}
									title="Additional Information"
								/>
							</Grid>
						</Paper>
					)}
				</Grid>

				{/* Right Column - Funding Summary */}
				<Grid item xs={12} md={5}>
					<FundingSummary
						accountBalance={accountBalance}
						currency={currency}
						earmarked={totals.earmarked}
						future={totals.future}
						pendingCount={totals.pendingCount}
						planned={totals.planned}
						total={totals.total}
					/>
				</Grid>
			</Grid>

			{/* Info: Monthly planning is work-plan driven */}
			<Alert severity="info" sx={{ mt: 3 }}>
				Create a spend plan first to start planning monthly. Once saved, you can allocate amounts
				per activity and month in the planning worksheet.
			</Alert>

			{/* Footer Note */}
			<Typography variant="caption" color="text.secondary">
				* Account allocations reduce available funds. Pending payments may increase the balance.
			</Typography>

			{error && (
				<Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}
		</form>
	);
};
